import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Newspaper,
  FileText,
  Bell,
  Megaphone,
  Plus,
  Search,
  Edit3,
  Trash2,
  Eye,
  X,
  Bold,
  Italic,
  Heading2,
  List,
  Link2,
  ImagePlus,
} from "lucide-react";
import {
  getPostsApi,
  createPostApi,
  updatePostApi,
  deletePostApi,
  uploadPostImageApi,
  uploadPostGalleryApi,
} from "../../../api/content.api";
import { getCategoriesApi, createCategoryApi } from "../../../api/category.api";

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/api\/v1\/?$/, "");

const sections = [
  { id: "news", title: "اخبار", icon: Newspaper, description: "مدیریت اخبار هیئت فوتبال", type: "NEWS" },
  { id: "blog", title: "وبلاگ", icon: FileText, description: "مدیریت مطالب و مقالات", type: "BLOG" },
  { id: "announcements", title: "اطلاعیه‌ها", icon: Bell, description: "مدیریت اطلاعیه‌های رسمی", type: "ANNOUNCEMENT" },
  { id: "advertisements", title: "تبلیغات", icon: Megaphone, description: "مدیریت تبلیغات سایت", type: null },
];

const STATUS_LABELS = {
  DRAFT: "پیش‌نویس",
  REVIEW: "در حال بررسی",
  PUBLISHED: "منتشر شده",
  ARCHIVED: "بایگانی شده",
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fa-IR");
  } catch {
    return "-";
  }
}

function mediaUrl(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_ORIGIN}${url}`;
}

function coverImageUrl(item) {
  return mediaUrl(item?.coverImage);
}

const emptyForm = {
  title: "",
  content: "",
  status: "DRAFT",
  publishDate: "",
  categoryId: "",
  tags: "",
};

function ToolbarButton({ icon: IconCmp, title, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-200"
    >
      <IconCmp size={15} />
    </button>
  );
}

const ContentTextarea = forwardRef(function ContentTextarea({ value, onChange }, ref) {
  const innerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertAtCursor(snippet) {
      const el = innerRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = value.slice(0, start) + snippet + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + snippet.length;
        el.selectionStart = el.selectionEnd = pos;
      });
    },
  }));

  function wrapSelection(before, after = before) {
    const el = innerRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || "متن";
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd = start + before.length + selected.length;
    });
  }

  function insertLinePrefix(prefix) {
    const el = innerRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + prefix.length;
    });
  }

  function insertLink() {
    const el = innerRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || "متن لینک";
    const snippet = `[${selected}](https://)`;
    const next = value.slice(0, start) + snippet + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => el.focus());
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-100 px-2 py-1.5">
        <ToolbarButton icon={Bold} title="بولد" onClick={() => wrapSelection("**")} />
        <ToolbarButton icon={Italic} title="ایتالیک" onClick={() => wrapSelection("*")} />
        <ToolbarButton icon={Heading2} title="تیتر" onClick={() => insertLinePrefix("## ")} />
        <ToolbarButton icon={List} title="لیست" onClick={() => insertLinePrefix("- ")} />
        <ToolbarButton icon={Link2} title="لینک" onClick={insertLink} />
      </div>
      <textarea
        ref={innerRef}
        rows={7}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="متن با Markdown پشتیبانی می‌شود: **بولد**, *ایتالیک*, ## تیتر, - لیست, [متن](لینک), ![توضیح](آدرس عکس)"
        className="w-full resize-y bg-slate-50 px-4 py-2.5 text-sm outline-none focus:bg-white"
      />
    </div>
  );
});

export default function ContentManagement() {
  const [activeSection, setActiveSection] = useState("news");
  const [search, setSearch] = useState("");
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryInputRef = useRef(null);
  const contentRef = useRef(null);

  const active = sections.find((item) => item.id === activeSection);
  const Icon = active.icon;
  const isAdSection = active.type === null;

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await getCategoriesApi();
      setCategories(data.data || []);
    } catch {
      // نبود دسته‌بندی مانع کار بقیه‌ی صفحه نمی‌شود
    }
  }, []);

  const loadPosts = useCallback(async () => {
    if (isAdSection) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await getPostsApi({ type: active.type });
      setPosts(data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "خطا در دریافت اطلاعات");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [active.type, isAdSection]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const items = posts.filter((item) => (item.title || "").toLowerCase().includes(search.toLowerCase()));

  function resetImageState() {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setGalleryFiles([]);
    setExistingGallery([]);
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  }

  function openCreate() {
    setEditingPost(null);
    setForm(emptyForm);
    setFormError("");
    resetImageState();
    setModalOpen(true);
  }

  function openEdit(post) {
    setEditingPost(post);
    setForm({
      title: post.title || "",
      content: post.content || "",
      status: post.status || "DRAFT",
      publishDate: post.publishDate ? post.publishDate.substring(0, 10) : "",
      categoryId: post.category?.id ? String(post.category.id) : "",
      tags: (post.tags || []).map((t) => t.title).join(", "),
    });
    setFormError("");
    resetImageState();
    setImagePreview(coverImageUrl(post));
    setExistingGallery(post.gallery || []);
    setModalOpen(true);
  }

  function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function handleGalleryFilesSelect(e) {
    const files = Array.from(e.target.files || []);
    setGalleryFiles(files);
  }

  async function uploadGalleryNow(postId) {
    if (galleryFiles.length === 0) return;
    setGalleryUploading(true);
    try {
      const { data } = await uploadPostGalleryApi(postId, galleryFiles);
      setExistingGallery(data.data?.gallery || []);
      setGalleryFiles([]);
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    } catch (err) {
      alert(err.response?.data?.message || "آپلود گالری ناموفق بود");
    } finally {
      setGalleryUploading(false);
    }
  }

  function insertGalleryImageToContent(url, index) {
    if (!contentRef.current) return;
    contentRef.current.insertAtCursor(`\n![تصویر ${index + 1}](${url})\n`);
  }

  async function handleAddCategory() {
    const title = window.prompt("عنوان دسته‌بندی جدید را وارد کنید:");
    if (!title || !title.trim()) return;
    try {
      const { data } = await createCategoryApi({ title: title.trim() });
      setCategories((prev) => [...prev, data.data]);
      setForm((prev) => ({ ...prev, categoryId: String(data.data.id) }));
    } catch (err) {
      alert(err.response?.data?.message || "ایجاد دسته‌بندی ناموفق بود");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("آیا از حذف این مورد مطمئن هستید؟")) return;
    try {
      await deletePostApi(id);
      loadPosts();
    } catch (err) {
      alert(err.response?.data?.message || "حذف ناموفق بود");
    }
  }

  async function handleSubmit() {
    setFormError("");
    if (!form.title.trim() || !form.content.trim()) {
      setFormError("عنوان و متن الزامی است");
      return;
    }
    setSubmitting(true);
    try {
      const basePayload = {
        title: form.title,
        content: form.content,
        status: form.status,
        publishDate: form.publishDate || null,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        tags: form.tags || "",
      };

      let postId = editingPost?.id;
      if (editingPost) {
        await updatePostApi(editingPost.id, basePayload);
      } else {
        const { data } = await createPostApi({ ...basePayload, type: active.type });
        postId = data.data?.id;
      }

      if (imageFile && postId) {
        try {
          await uploadPostImageApi(postId, imageFile);
        } catch (imgErr) {
          setFormError(
            "پست ذخیره شد اما آپلود عکس شاخص ناموفق بود: " +
              (imgErr.response?.data?.message || "خطای نامشخص")
          );
          loadPosts();
          setSubmitting(false);
          return;
        }
      }

      if (galleryFiles.length > 0 && postId) {
        try {
          await uploadPostGalleryApi(postId, galleryFiles);
        } catch (galErr) {
          setFormError(
            "پست ذخیره شد اما آپلود گالری ناموفق بود: " +
              (galErr.response?.data?.message || "خطای نامشخص")
          );
          loadPosts();
          setSubmitting(false);
          return;
        }
      }

      setModalOpen(false);
      loadPosts();
    } catch (err) {
      setFormError(err.response?.data?.message || "ثبت اطلاعات ناموفق بود");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-80px)] flex-col gap-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
              <Icon size={27} />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900">مدیریت محتوا</h1>
              <p className="mt-1 text-sm text-slate-500">{active.description}</p>
            </div>
          </div>

          {!isAdSection && (
            <button
              className="flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
              type="button"
              onClick={openCreate}
            >
              <Plus size={19} />
              ایجاد {active.title}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[260px_1fr]">
        <aside className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-3 px-3 py-3">
            <span className="text-xs font-bold text-slate-400">بخش‌ های محتوا</span>
          </div>
          <div className="space-y-2">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              const selected = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => {
                    setActiveSection(section.id);
                    setSearch("");
                  }}
                  className={`group flex w-full items-center gap-3 rounded-2xl px-4 py-4 text-right transition ${
                    selected ? "bg-slate-900 text-white shadow-lg" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                      selected ? "bg-white/10" : "bg-slate-100 group-hover:bg-white"
                    }`}
                  >
                    <SectionIcon size={20} />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-bold">{section.title}</span>
                    <span className={`mt-1 block text-[11px] ${selected ? "text-white/60" : "text-slate-400"}`}>
                      {section.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="min-w-0 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-black text-slate-900">{active.title}</h2>
              <p className="mt-1 text-xs text-slate-400">مدیریت و ویرایش محتوای این بخش</p>
            </div>

            {!isAdSection && (
              <div className="relative w-full md:w-72">
                <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`جستجو در ${active.title}...`}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-4 pr-11 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
            )}
          </div>

          {isAdSection ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 text-center">
              <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-white text-slate-300 shadow-sm">
                <Icon size={34} />
              </div>
              <h3 className="text-base font-black text-slate-700">مدیریت تبلیغات جداست</h3>
              <p className="mt-2 max-w-xs text-sm text-slate-400">
                تبلیغات ساختار متفاوتی نسبت به اخبار/وبلاگ دارد و از صفحه‌ی «تبلیغات» در منوی اصلی مدیریت می‌شود.
              </p>
            </div>
          ) : loading ? (
            <div className="flex min-h-[300px] items-center justify-center text-sm text-slate-400">
              در حال بارگذاری...
            </div>
          ) : error ? (
            <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
          ) : items.length > 0 ? (
            <div className="space-y-3">
              {items.map((item) => {
                const thumb = coverImageUrl(item);
                return (
                  <div
                    key={item.id}
                    className="group flex flex-col gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 transition hover:border-slate-200 hover:bg-white hover:shadow-sm md:flex-row md:items-center"
                  >
                    {thumb ? (
                      <img src={thumb} alt={item.title} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                    ) : (
                      <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-slate-200 text-slate-400">
                        <ImagePlus size={20} />
                      </div>
                    )}

                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800">{item.title}</h3>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                        <span>{formatDate(item.publishDate || item.createdAt)}</span>
                        <span
                          className={`rounded-full px-3 py-1 ${
                            item.status === "PUBLISHED"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                        {item.category?.title && (
                          <span className="rounded-full bg-slate-200 px-3 py-1 text-slate-600">
                            {item.category.title}
                          </span>
                        )}
                        {(item.tags || []).map((t) => (
                          <span key={t.id} className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-600">
                            #{t.title}
                          </span>
                        ))}
                        {(item.gallery || []).length > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-slate-600">
                            <ImagePlus size={12} />
                            {item.gallery.length}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm transition hover:bg-slate-900 hover:text-white"
                        title="مشاهده و ویرایش"
                      >
                        <Eye size={17} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm transition hover:bg-blue-600 hover:text-white"
                        title="ویرایش"
                      >
                        <Edit3 size={17} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm transition hover:bg-red-600 hover:text-white"
                        title="حذف"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50">
              <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-white text-slate-300 shadow-sm">
                <Icon size={34} />
              </div>
              <h3 className="text-base font-black text-slate-700">محتوایی وجود ندارد</h3>
              <p className="mt-2 text-sm text-slate-400">هنوز موردی در بخش {active.title} ثبت نشده است.</p>
              <button
                type="button"
                onClick={openCreate}
                className="mt-5 flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
              >
                <Plus size={17} />
                ایجاد اولین مورد
              </button>
            </div>
          )}
        </section>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-black text-slate-900">
                {editingPost ? `ویرایش ${active.title}` : `ایجاد ${active.title}`}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            </div>

            {formError && (
              <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{formError}</div>
            )}

            <div className="flex flex-col gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600">عکس شاخص (نمایش در کارت و بالای مطلب)</label>
                <div className="flex items-center gap-4">
                  {imagePreview ? (
                    <img src={imagePreview} alt="پیش‌نمایش" className="h-20 w-20 rounded-xl object-cover" />
                  ) : (
                    <div className="grid h-20 w-20 place-items-center rounded-xl bg-slate-100 text-slate-300">
                      <ImagePlus size={24} />
                    </div>
                  )}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="cover-image-input"
                    />
                    <label
                      htmlFor="cover-image-input"
                      className="cursor-pointer rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                    >
                      انتخاب عکس
                    </label>
                    <p className="mt-1.5 text-xs text-slate-400">JPG یا PNG</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600">
                  گالری تصاویر (برای استفاده داخل متن، هر جا لازم بود)
                </label>
                <div className="flex flex-wrap gap-2">
                  {existingGallery.map((g, i) => (
                    <div key={g.id} className="group relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200">
                      <img src={mediaUrl(g.url)} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => insertGalleryImageToContent(g.url, i)}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 text-[10px] font-bold text-white opacity-0 transition group-hover:opacity-100"
                      >
                        افزودن به متن
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryFilesSelect}
                    className="hidden"
                    id="gallery-input"
                  />
                  <label
                    htmlFor="gallery-input"
                    className="cursor-pointer rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    انتخاب چند عکس
                  </label>
                  {galleryFiles.length > 0 && (
                    <span className="text-xs text-slate-400">{galleryFiles.length} فایل انتخاب شد</span>
                  )}
                  {editingPost && galleryFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={() => uploadGalleryNow(editingPost.id)}
                      disabled={galleryUploading}
                      className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      {galleryUploading ? "در حال آپلود..." : "آپلود گالری"}
                    </button>
                  )}
                </div>
                {!editingPost && galleryFiles.length > 0 && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    گالری بعد از ثبت پست، به‌طور خودکار آپلود می‌شود.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600">عنوان</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600">متن</label>
                <ContentTextarea ref={contentRef} value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-600">دسته‌بندی (اختیاری)</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={form.categoryId}
                      onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                    >
                      <option value="">بدون دسته‌بندی</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      title="دسته‌بندی جدید"
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-600">وضعیت</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                  >
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600">
                  برچسب‌ها (اختیاری — با کاما جدا کنید)
                </label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="مثلاً: لیگ برتر, باشگاه پاوه, قهرمانی"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600">تاریخ انتشار (اختیاری)</label>
                <input
                  type="date"
                  value={form.publishDate}
                  onChange={(e) => setForm({ ...form, publishDate: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:bg-white"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-100"
              >
                انصراف
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {submitting ? "در حال ثبت..." : editingPost ? "ذخیره تغییرات" : "ثبت"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
